import { useState } from "react";
import { Info, Maximize2, Sparkles } from "lucide-react";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import {
    Area,
    AreaChart,
    ResponsiveContainer,
    Tooltip,
} from "recharts";
import { cn } from "../../lib/utils";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { ANALYTICS_BADGE_STYLES, ANALYTICS_COLORS } from "./analyticsTheme";

export interface MetricTrendPoint {
    label: string;
    value: number;
}

interface MetricCardProps {
    title: string;
    value: string;
    hint?: string;
    tone?: keyof typeof ANALYTICS_BADGE_STYLES;
    trend?: MetricTrendPoint[];
    summaries?: string[];
}

export const MetricCard = ({
    title,
    value,
    hint,
    tone = "neutral",
    trend,
    summaries = [],
}: MetricCardProps) => (
    <Card className="relative overflow-hidden border-border/70">
        <CardContent className="p-5">
            <div className="flex items-start justify-between gap-3">
                <div className="space-y-1">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                        {title}
                    </p>
                    <p className="text-3xl font-semibold tracking-tight text-foreground">{value}</p>
                </div>
                <span
                    className={cn(
                        "inline-flex rounded-full border px-2 py-1 text-[11px] font-medium",
                        ANALYTICS_BADGE_STYLES[tone]
                    )}
                >
                    {tone}
                </span>
            </div>
            {hint && <p className="mt-2 text-xs text-muted-foreground">{hint}</p>}
            {trend && trend.length > 1 && (
                <div className="mt-4 h-16 w-full">
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={trend}>
                            <Tooltip
                                formatter={(value?: number) => [value ?? "-", title]}
                                labelFormatter={(label) => `${label}`}
                            />
                            <Area
                                type="monotone"
                                dataKey="value"
                                stroke={ANALYTICS_COLORS.info}
                                fill={ANALYTICS_COLORS.trendFill}
                                strokeWidth={2}
                            />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            )}
            {summaries.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2 text-[11px] text-muted-foreground">
                    {summaries.map((summary) => (
                        <span key={summary} className="rounded-full bg-muted px-2 py-1">
                            {summary}
                        </span>
                    ))}
                </div>
            )}
        </CardContent>
    </Card>
);

interface ChartCardProps {
    title: string;
    description?: string;
    action?: React.ReactNode;
    children: React.ReactNode;
    contentClassName?: string;
}

export function ChartCard({
    title,
    description,
    action,
    children,
    contentClassName,
}: ChartCardProps) {
    const [open, setOpen] = useState(false);

    return (
        <>
            <Card className="border-border/70">
                <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-3">
                    <div className="space-y-1">
                        <CardTitle className="text-base font-semibold">{title}</CardTitle>
                        {description ? <CardDescription className="text-xs">{description}</CardDescription> : null}
                    </div>
                    <div className="flex items-center gap-1">
                        {action}
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-muted-foreground hover:text-foreground"
                            onClick={() => setOpen(true)}
                        >
                            <Maximize2 className="h-3.5 w-3.5" />
                        </Button>
                    </div>
                </CardHeader>
                <CardContent className={cn("pt-0", contentClassName)}>{children}</CardContent>
            </Card>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent
                    className="!flex !max-w-[90vw] !flex-col !gap-0 !p-0"
                    style={{ width: "90vw", height: "90vh" }}
                    showCloseButton={true}
                >
                    <DialogHeader className="shrink-0 border-b px-6 py-4">
                        <DialogTitle>{title}</DialogTitle>
                        {description ? <p className="text-sm text-muted-foreground">{description}</p> : null}
                    </DialogHeader>
                    <div className="h-0 flex-1 overflow-auto p-6">
                        {children}
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
}

interface PlaceholderCardProps {
    title: string;
    description: string;
}

export const PlaceholderCard = ({ title, description }: PlaceholderCardProps) => (
    <Card className="border-dashed border-border/70 bg-muted/20">
        <CardContent className="flex min-h-44 flex-col justify-between p-6">
            <div className="space-y-3">
                <div className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-muted text-muted-foreground">
                    <Sparkles className="h-5 w-5" />
                </div>
                <div>
                    <p className="text-base font-semibold text-foreground">{title}</p>
                    <p className="mt-1 text-sm text-muted-foreground">{description}</p>
                </div>
            </div>
            <div className="mt-5 flex items-center gap-2 text-xs text-muted-foreground">
                <Info className="h-3.5 w-3.5" />
                Waiting on a later data path or instrumentation ticket.
            </div>
        </CardContent>
    </Card>
);

interface StatusBadgeProps {
    label: string;
    tone?: keyof typeof ANALYTICS_BADGE_STYLES;
}

export const StatusBadge = ({ label, tone = "neutral" }: StatusBadgeProps) => (
    <span
        className={cn(
            "inline-flex rounded-full border px-2 py-1 text-[11px] font-medium",
            ANALYTICS_BADGE_STYLES[tone]
        )}
    >
        {label}
    </span>
);

interface SectionHeaderProps {
    title: string;
    description?: string;
    actions?: React.ReactNode;
}

export const SectionHeader = ({ title, description, actions }: SectionHeaderProps) => (
    <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div className="space-y-1">
            <h2 className="text-xl font-semibold tracking-tight text-foreground">{title}</h2>
            {description ? <p className="text-sm text-muted-foreground">{description}</p> : null}
        </div>
        {actions}
    </div>
);
